/**
 * @fileoverview Test Flipper - Mirror tests for Dark player perspective
 *
 * Transforms tests so they run from Dark's perspective instead of Light's.
 * This effectively doubles test coverage by catching color-specific bugs.
 *
 * Transformations:
 * - FEN: Reverse rows (row 0↔7), swap piece colors (case swap)
 * - Moves: Swap "Light"↔"Dark", flip row numbers (1↔8)
 * - Delta: Swap color references in description strings
 * - Result: Swap victory types and winner
 * - Kings: Swap lightKing↔darkKing
 */

import { IntegrationTest, SequenceStep, ExpectedDelta, TestResult, GameEndType } from "./types";
import { AIDecisionTest } from "./AIDecisionTypes";
import { PieceType } from "../zodiacEngine/types";

/**
 * Direction arrow mappings for vertical flip
 * When board flips, up becomes down, etc.
 */
const DIRECTION_FLIP: Record<string, string> = {
  "↑": "↓",
  "↓": "↑",
  "↗": "↘",
  "↘": "↗",
  "↖": "↙",
  "↙": "↖",
  "←": "←", // Horizontal stays same
  "→": "→",
};

/**
 * Flip a single FEN string to mirror the board
 *
 * Zodiac FEN format: column-by-column, bottom-to-top per column
 * Example: "8/8/BK6ck/8/8" = 5 columns (A-E)
 *
 * To flip:
 * 1. Reverse each column's content (row 0 becomes row 7)
 * 2. Swap piece case (B↔b, K stays contextual)
 * 3. Flip penguin directions
 */
export function flipFEN(fen: string): string {
  // Handle turn indicator prefix
  // No prefix or 'l' = Light's turn → flipped = 'd' (Dark's turn)
  // 'd' prefix = Dark's turn → flipped = 'l' (Light's turn)
  let turnPrefix = "d"; // Default: if original has no indicator (Light's turn), flipped is Dark's turn
  let fenBody = fen;
  if (fen.startsWith("d")) {
    turnPrefix = "l"; // Dark's turn → Light's turn
    fenBody = fen.substring(1);
  } else if (fen.startsWith("l")) {
    turnPrefix = "d"; // Light's turn → Dark's turn
    fenBody = fen.substring(1);
  }
  // If no prefix, turnPrefix stays "d" (original was Light's turn, flipped is Dark's turn)

  // Split into columns
  const columns = fenBody.split("/");

  // Process each column
  const flippedColumns = columns.map((column) => flipColumn(column));

  return turnPrefix + flippedColumns.join("/");
}

/**
 * Flip a single column of FEN
 * Reverses the order and swaps colors
 */
function flipColumn(column: string): string {
  // Parse column into tokens (pieces, numbers, symbols)
  const tokens = parseColumnTokens(column);

  // Reverse token order
  const reversedTokens = tokens.reverse();

  // Swap colors and flip directions
  const flippedTokens = reversedTokens.map((token) => flipToken(token));

  return flippedTokens.join("");
}

/**
 * Parse a FEN column into tokens
 * Each token is either a number (empty squares) or a piece with its modifiers
 *
 * IMPORTANT: Stacking indicator (+) means the following piece is ON TOP of the previous piece.
 * So "B♛+V" = Bear with Virus stacked on it, occupying the SAME square.
 * When reversing columns, the entire stacked unit must stay together.
 * Therefore, +X is parsed as a SUFFIX of the previous piece token.
 */
function parseColumnTokens(column: string): string[] {
  const tokens: string[] = [];
  let i = 0;

  while (i < column.length) {
    const char = column[i];

    // Number = empty squares
    if (/\d/.test(char)) {
      tokens.push(char);
      i++;
      continue;
    }

    // Piece letter - collect with all following modifiers AND any stacked pieces
    if ("BCFUGMOPRSTWVbcfugmoprstwv".includes(char)) {
      let token = char;
      i++;

      // Collect modifiers: direction arrows, status symbols, king markers, etc.
      while (i < column.length) {
        const next = column[i];
        // Stop if we hit a digit (empty squares)
        if (/\d/.test(next)) break;
        // Stop if we hit another piece letter that's NOT preceded by +
        if ("BCFUGMOPRSTWVbcfugmoprstwv".includes(next)) break;

        // If we see a stacking indicator, include it AND the stacked piece as part of this token
        if (next === "+") {
          token += next;
          i++;
          // Get the stacked piece
          if (i < column.length && "BCFUGMOPRSTWVbcfugmoprstwv".includes(column[i])) {
            token += column[i];
            i++;
            // Collect modifiers for the stacked piece
            while (i < column.length) {
              const mod = column[i];
              if (/\d/.test(mod)) break;
              if ("BCFUGMOPRSTWVbcfugmoprstwv".includes(mod)) break;
              if (mod === "+") break; // Another stacking - continue outer loop
              token += mod;
              i++;
            }
          }
          continue; // Check for more stacked pieces
        }

        // Include the modifier
        token += next;
        i++;
      }

      tokens.push(token);
      continue;
    }

    // Other symbol (shouldn't happen normally)
    tokens.push(char);
    i++;
  }

  return tokens;
}

/**
 * Flip a single token (piece with modifiers)
 * - Keep stacking indicator (+) in place
 * - Swap piece case (color)
 * - Swap king markers (K↔k, ♛↔♕)
 * - Flip direction arrows
 * - Keep status symbols
 */
function flipToken(token: string): string {
  if (/^\d$/.test(token)) {
    return token; // Numbers stay the same
  }

  let result = "";

  for (const char of token) {
    // Keep stacking indicator as-is
    if (char === "+") {
      result += "+";
    }
    // Swap piece case (Light↔Dark)
    else if (/[A-Z]/.test(char) && "BCFUGMOPRSTWV".includes(char)) {
      result += char.toLowerCase();
    } else if (/[a-z]/.test(char) && "bcfugmoprstwv".includes(char)) {
      result += char.toUpperCase();
    }
    // Swap king markers (K = light king, k = dark king)
    else if (char === "K") {
      result += "k";
    } else if (char === "k") {
      result += "K";
    }
    // Swap Unicode king markers (♛ = light king marker, ♕ = dark king marker in Zodiac)
    else if (char === "♛") {
      result += "♕";
    } else if (char === "♕") {
      result += "♛";
    }
    // Flip direction arrows
    else if (DIRECTION_FLIP[char]) {
      result += DIRECTION_FLIP[char];
    }
    // Keep everything else (status symbols, etc.)
    else {
      result += char;
    }
  }

  return result;
}

/**
 * Move type direction suffix mappings for flip
 * When board flips, up becomes down in the move type specifier
 */
const MOVE_TYPE_DIRECTION_FLIP: Record<string, string> = {
  "entangle-up": "entangle-down",
  "entangle-down": "entangle-up",
  "entangle-left": "entangle-right",
  "entangle-right": "entangle-left",
};

/**
 * Flip a move notation string
 * "Light Bear C1->C2" becomes "Dark Bear C8->C7"
 * "Light Octopus C3->E3:entangle-up" becomes "Dark Octopus C6->E6:entangle-down"
 */
export function flipMoveNotation(move: string): string {
  // Handle special commands
  if (move.startsWith("timeout:")) {
    const color = move.includes("light") ? "dark" : "light";
    return `timeout:${color}`;
  }

  // Swap color
  let flipped = move
    .replace(/\bLight\b/gi, "DARK_TEMP")
    .replace(/\bDark\b/gi, "Light")
    .replace(/DARK_TEMP/g, "Dark");

  // Flip row numbers (1↔8, 2↔7, 3↔6, 4↔5)
  flipped = flipped.replace(/([A-Ea-e])([1-8])/g, (_, col, row) => {
    const flippedRow = 9 - parseInt(row);
    return `${col}${flippedRow}`;
  });

  // Flip directional move type specifiers (e.g., entangle-up -> entangle-down)
  flipped = flipped.replace(/:(\w+-\w+)$/i, (_, specifier) => {
    const flippedSpecifier = MOVE_TYPE_DIRECTION_FLIP[specifier.toLowerCase()];
    return flippedSpecifier ? `:${flippedSpecifier}` : `:${specifier}`;
  });

  return flipped;
}

/**
 * Flip an expected delta
 */
export function flipDelta(delta: ExpectedDelta | undefined): ExpectedDelta | undefined {
  if (!delta) return undefined;

  const flipped: ExpectedDelta = { ...delta };

  // Flip color references in pieceMoved
  if (delta.pieceMoved) {
    flipped.pieceMoved = flipColorReferences(delta.pieceMoved);
  }

  // Flip pieceCaptured
  if (delta.pieceCaptured) {
    flipped.pieceCaptured = flipColorReferences(delta.pieceCaptured);
  }

  // Flip statusApplied
  if (delta.statusApplied) {
    flipped.statusApplied = delta.statusApplied.map(flipColorReferences);
  }

  return flipped;
}

/**
 * Direction word mappings for text flip
 */
const DIRECTION_WORD_FLIP: Record<string, string> = {
  "up": "down",
  "down": "up",
  "up-left": "down-right",
  "up-right": "down-left",
  "down-left": "up-right",
  "down-right": "up-left",
  "Up": "Down",
  "Down": "Up",
  "Up-left": "Down-right",
  "Up-right": "Down-left",
  "Down-left": "Up-right",
  "Down-right": "Up-left",
};

/**
 * Flip color references in a string and flip coordinates
 */
function flipColorReferences(str: string): string {
  let result = str
    .replace(/\bLight\b/g, "DARK_TEMP")
    .replace(/\bDark\b/g, "Light")
    .replace(/DARK_TEMP/g, "Dark");

  // Flip coordinates in brackets like [C1 -> C2] or [C1]
  result = result.replace(/\[([A-E])(\d)(?:\s*->\s*([A-E])(\d))?\]/g, (match, col1, row1, col2, row2) => {
    const flippedRow1 = 9 - parseInt(row1);
    if (col2 && row2) {
      const flippedRow2 = 9 - parseInt(row2);
      return `[${col1}${flippedRow1} -> ${col2}${flippedRow2}]`;
    }
    return `[${col1}${flippedRow1}]`;
  });

  // Flip direction words (up↔down, up-left↔down-right, etc.)
  // Use word boundaries to avoid partial matches
  result = result.replace(/\b(up-left|up-right|down-left|down-right|up|down)\b/gi, (match) => {
    return DIRECTION_WORD_FLIP[match] || match;
  });

  return result;
}

/**
 * Flip a test result (game end type and winner)
 */
export function flipResult(result: TestResult | undefined): TestResult | undefined {
  if (!result) return undefined;

  const flipped: TestResult = { ...result };

  // Flip winner
  if (result.winner) {
    flipped.winner = result.winner === "light" ? "dark" : "light";
  }

  // Flip game end type
  if (result.gameEnd) {
    flipped.gameEnd = flipGameEndType(result.gameEnd);
  }

  return flipped;
}

/**
 * Flip a game end type
 */
function flipGameEndType(gameEnd: GameEndType): GameEndType {
  if (gameEnd === false) return false;

  const flipMap: Record<string, GameEndType> = {
    victory_race_light: "victory_race_dark",
    victory_race_dark: "victory_race_light",
    victory_zombierace_light: "victory_zombierace_dark",
    victory_zombierace_dark: "victory_zombierace_light",
    victory_kingslayer_light: "victory_kingslayer_dark",
    victory_kingslayer_dark: "victory_kingslayer_light",
    victory_timeout_light: "victory_timeout_dark",
    victory_timeout_dark: "victory_timeout_light",
    draw_stalemate: "draw_stalemate",
    draw_repetition: "draw_repetition",
  };

  return flipMap[gameEnd] || gameEnd;
}

/**
 * Flip a sequence step
 */
export function flipSequenceStep(step: SequenceStep): SequenceStep {
  return {
    move: flipMoveNotation(step.move),
    expectedFEN: flipFEN(step.expectedFEN),
    expectedDelta: flipDelta(step.expectedDelta),
    expectedResult: flipResult(step.expectedResult),
    note: step.note ? flipColorReferences(step.note) : undefined,
    expectedStatus: step.expectedStatus?.map(flipColorReferences),
  };
}

/**
 * Flip an entire integration test
 * Creates a mirrored version that tests from Dark's perspective
 */
export function flipTest(test: IntegrationTest): IntegrationTest {
  const flipped: IntegrationTest = {
    ...test,
    name: `${test.name} (Flipped)`,
    description: test.description ? `[FLIPPED] ${test.description}` : undefined,
    inputFEN: flipFEN(test.inputFEN),
    expectedFEN: flipFEN(test.expectedFEN),
    move: flipMoveNotation(test.move),
    expectedDelta: flipDelta(test.expectedDelta),
    expectedResult: flipResult(test.expectedResult),
    // Swap kings
    lightKing: test.darkKing,
    darkKing: test.lightKing,
  };

  // Flip sequence steps if present
  if (test.isSequence && test.steps) {
    flipped.steps = test.steps.map(flipSequenceStep);
  }

  return flipped;
}

/**
 * Generate both original and flipped versions of a test
 */
export function generateBothPerspectives(test: IntegrationTest): IntegrationTest[] {
  return [test, flipTest(test)];
}

/**
 * Generate flipped versions for an entire test suite
 */
export function flipTestSuite(tests: IntegrationTest[]): IntegrationTest[] {
  return tests.flatMap(generateBothPerspectives);
}

/**
 * Flip an AI decision test from one perspective to the other.
 * Mirrors the board (row flip + color swap) and adjusts expected destination.
 */
export function flipAIDecisionTest(test: AIDecisionTest): AIDecisionTest {
  return {
    ...test,
    name: `${test.name} (Light)`,
    inputFEN: flipFEN(test.inputFEN),
    expectedDestination: test.expectedDestination ? {
      row: 7 - test.expectedDestination.row,
      col: test.expectedDestination.col,
    } : undefined,
    forbiddenDestinations: test.forbiddenDestinations?.map(f => ({
      row: 7 - f.row,
      col: f.col,
    })),
    side: test.side === "dark" ? "light" : "dark",
    lightKing: test.darkKing,
    darkKing: test.lightKing,
  };
}
